import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  FormControlLabel,
  Switch,
  TextField,
  Divider,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { useSnackbar } from "notistack";
import { set_general_review_date_api } from "src/DAL/programmeWorkout/programmeWorkout";
import CustomPopoverNew from "src/components/CustomPopoverNew";
import moment from "moment";

export default function ReviewDateMemberModal({
  member,
  isOpenPop,
  isClosePop,
  onSuccess,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [programmeSettings, setProgrammeSettings] = useState([]);

  useEffect(() => {
    if (member?.programs?.length) {
      const mapped = member.programs.map((p) => ({
        _id: p._id,
        title: p.title,
        original_toggle: p?.use_individual_review_date || false,
        use_individual_review_date: p?.use_individual_review_date || false,

        // ✅ toggle OFF pe programme ki general_review_date dikhao
        general_review_date: p?.review_settings?.general_review_date
          ? new Date(p.review_settings.general_review_date)
          : null,

        // ✅ toggle ON pe member ki custom review_date dikhao
        custom_review_date:
          p?.use_individual_review_date && p.review_date
            ? new Date(p.review_date)
            : null,

        // ✅ display ke liye: on ho to custom, off ho to general
        review_date: p?.use_individual_review_date
          ? p.review_date
            ? new Date(p.review_date)
            : null
          : p?.review_settings?.general_review_date
          ? new Date(p.review_settings.general_review_date)
          : null,

        existing_review_date: p.review_date ? new Date(p.review_date) : null,
      }));
      setProgrammeSettings(mapped);
    }
  }, [member]);

  const updateProgramme = (id, key, value) => {
    setProgrammeSettings((prev) =>
      prev.map((p) => {
        if (p._id !== id) return p;
        const updated = { ...p, [key]: value };

        if (key === "use_individual_review_date") {
          if (value) {
            // ✅ toggle ON → member ki saved custom date dikhao
            updated.review_date = p.custom_review_date || null;
          } else {
            // ✅ toggle OFF → programme ki general date dikhao
            updated.review_date = p.general_review_date;
          }
        }

        if (key === "review_date") {
          // ✅ custom date update hone par preserve karo
          updated.custom_review_date = value;
        }

        return updated;
      }),
    );
  };
  const handleSubmit = async () => {
    const invalid = programmeSettings.find(
      (p) => p.use_individual_review_date && !p.review_date,
    );
    if (invalid) {
      enqueueSnackbar(`Please select a review date for "${invalid.title}".`, {
        variant: "error",
      });
      return;
    }

    setIsFormLoading(true);

    try {
      const promises = programmeSettings.map((p) =>
        set_general_review_date_api(
          {
            general_review_date: p.review_date,
            use_individual_review_date: p.use_individual_review_date,
            memberId: member?._id,
          },
          p._id,
          "member",
        ),
      );

      const results = await Promise.all(promises);

      const failed = results.find((r) => r?.code !== 200);
      if (failed) {
        enqueueSnackbar(failed.message, {
          variant: "error",
        });
      } else {
        enqueueSnackbar("Review dates updated successfully.", {
          variant: "success",
        });
        onSuccess?.({ changed: programmeSettings });
        isClosePop();
      }
    } catch (error) {}

    setIsFormLoading(false);
  };

  return (
    <CustomPopoverNew
      isOpenPop={isOpenPop}
      isClosePop={isClosePop}
      title="Set Review Dates"
      width="680px"
      componentToPassDown={
        <div className="px-4 pb-4">
          <p className="text-muted mt-2 mb-3" style={{ fontSize: 13 }}>
            By default, each programme uses its existing review date. Enable{" "}
            <strong>Custom Review Date</strong> on a programme to override it
            for this member. Disabling will revert to the programme's default
            date.
          </p>

          <div style={{ maxHeight: 420, overflowY: "auto", paddingRight: 4 }}>
            {programmeSettings.length === 0 ? (
              <p className="text-muted text-center py-4">
                No programmes found for this member.
              </p>
            ) : (
              programmeSettings.map((p) => (
                <div
                  key={p._id}
                  style={{
                    border: p.use_individual_review_date
                      ? "1.5px solid #1976d2"
                      : "1px solid #e0e0e0",
                    padding: "12px 14px",
                    marginBottom: 10,
                    borderRadius: 8,
                    backgroundColor: p.use_individual_review_date
                      ? "#f0f6ff"
                      : "#fafafa",
                    transition: "all 0.2s ease",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 8,
                    }}
                  >
                    <div>
                      <strong style={{ fontSize: 14 }}>{p.title}</strong>
                      {/* Toggle OFF pe general date dikhao, ON pe member ki custom date */}
                      {(p.use_individual_review_date
                        ? p.custom_review_date
                        : p.general_review_date) && (
                        <div
                          style={{ fontSize: 11, color: "#888", marginTop: 2 }}
                        >
                          {p.use_individual_review_date
                            ? "Custom"
                            : "Programme Default"}
                          :{" "}
                          {moment(
                            p.use_individual_review_date
                              ? p.custom_review_date
                              : p.general_review_date,
                          ).format("MM/DD/YYYY")}
                        </div>
                      )}
                    </div>

                    <FormControlLabel
                      style={{ margin: 0 }}
                      control={
                        <Switch
                          size="small"
                          checked={p.use_individual_review_date}
                          onChange={(e) =>
                            updateProgramme(
                              p._id,
                              "use_individual_review_date",
                              e.target.checked,
                            )
                          }
                        />
                      }
                      label={<span style={{ fontSize: 13 }}>Custom Date</span>}
                    />
                  </div>

                  {p.use_individual_review_date && (
                    <div className="mt-2">
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label="Custom Review Date *"
                          inputFormat="dd-MM-yyyy"
                          value={p.review_date}
                          onChange={(val) =>
                            updateProgramme(p._id, "review_date", val)
                          }
                          className="w-100"
                          disablePast
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              size="small"
                              error={!p.review_date}
                              helperText={
                                !p.review_date ? "Date is required" : ""
                              }
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          <Divider className="my-3" />

          <div className="text-end">
            <Button
              variant="outlined"
              onClick={isClosePop}
              disabled={isFormLoading}
              style={{ marginRight: 10 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isFormLoading || programmeSettings.length === 0}
              variant="contained"
              startIcon={
                isFormLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : null
              }
            >
              {isFormLoading ? "Saving..." : "Save Review Dates"}
            </Button>
          </div>
        </div>
      }
    />
  );
}
